/**
 * Certificate - Generates a downloadable A1 English course certificate.
 * Renders as SVG, can be exported as PNG via canvas.
 */
import { useRef, useState } from 'react'
import { Download, X, Loader2 } from 'lucide-react'

interface CertificateProps {
  studentName: string
  achievement: { en: string; es: string }
  date: string
  lang: 'en' | 'es'
  onClose: () => void
}

// Certificate stage definitions
export interface CertificateStage {
  id: string
  titleEn: string
  titleEs: string
  descEn: string
  descEs: string
  requirementType: string
  requirementValue: number
  color: string
}

export const certificateStages: CertificateStage[] = [
  {
    id: 'unit-1',
    titleEn: 'Certificate - Unit 1',
    titleEs: 'Certificado - Unidad 1',
    descEn: 'First Steps in English',
    descEs: 'Primeros Pasos en Inglés',
    requirementType: 'units_completed',
    requirementValue: 1,
    color: '#3b82f6',
  },
  {
    id: 'unit-2',
    titleEn: 'Certificate - Unit 2',
    titleEs: 'Certificado - Unidad 2',
    descEn: 'Daily Life',
    descEs: 'La Vida Cotidiana',
    requirementType: 'units_completed',
    requirementValue: 2,
    color: '#8b5cf6',
  },
  {
    id: 'unit-3',
    titleEn: 'Certificate - Unit 3',
    titleEs: 'Certificado - Unidad 3',
    descEn: 'Going Out and Leisure',
    descEs: 'Salidas y Ocio',
    requirementType: 'units_completed',
    requirementValue: 3,
    color: '#f59e0b',
  },
  {
    id: 'unit-4',
    titleEn: 'Certificate - Unit 4',
    titleEs: 'Certificado - Unidad 4',
    descEn: 'Projects and Future',
    descEs: 'Proyectos y Futuro',
    requirementType: 'units_completed',
    requirementValue: 4,
    color: '#ef4444',
  },
  {
    id: 'final',
    titleEn: 'Applied English Diploma',
    titleEs: 'Diploma Inglés Aplicado',
    descEn: 'Intensive Course Completed',
    descEs: 'Curso Intensivo Completado',
    requirementType: 'final_exam_passed',
    requirementValue: 1,
    color: '#d97706',
  },
]

function CertificateSVG({
  studentName,
  achievement,
  date,
  lang,
  color = '#0055A4',
  svgRef,
}: {
  studentName: string
  achievement: { en: string; es: string }
  date: string
  lang: 'en' | 'es'
  color?: string
  svgRef: React.RefObject<SVGSVGElement | null>
}) {
  const formattedDate = new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date))

  return (
    <svg
      ref={svgRef}
      width="800"
      height="566"
      viewBox="0 0 800 566"
      xmlns="http://www.w3.org/2000/svg"
      style={{ background: 'white' }}
    >
      {/* Background */}
      <rect width="800" height="566" fill="white" />

      {/* Outer border */}
      <rect x="15" y="15" width="770" height="536" fill="none" stroke={color} strokeWidth="3" rx="4" />
      <rect x="22" y="22" width="756" height="522" fill="none" stroke={color} strokeWidth="1" rx="2" opacity="0.4" />

      {/* Corner decorations */}
      {/* Top-left */}
      <g transform="translate(30, 30)">
        <path d="M0,30 L0,5 Q0,0 5,0 L30,0" fill="none" stroke={color} strokeWidth="2" />
        <circle cx="5" cy="5" r="3" fill={color} />
      </g>
      {/* Top-right */}
      <g transform="translate(770, 30) scale(-1,1)">
        <path d="M0,30 L0,5 Q0,0 5,0 L30,0" fill="none" stroke={color} strokeWidth="2" />
        <circle cx="5" cy="5" r="3" fill={color} />
      </g>
      {/* Bottom-left */}
      <g transform="translate(30, 536) scale(1,-1)">
        <path d="M0,30 L0,5 Q0,0 5,0 L30,0" fill="none" stroke={color} strokeWidth="2" />
        <circle cx="5" cy="5" r="3" fill={color} />
      </g>
      {/* Bottom-right */}
      <g transform="translate(770, 536) scale(-1,-1)">
        <path d="M0,30 L0,5 Q0,0 5,0 L30,0" fill="none" stroke={color} strokeWidth="2" />
        <circle cx="5" cy="5" r="3" fill={color} />
      </g>

      {/* US flag at top */}
      {/* Red/white stripes (13 total, compressed to 7 visible for the small size) */}
      <rect x="340" y="50" width="54" height="3" fill="#B22234" />
      <rect x="340" y="56" width="54" height="3" fill="#B22234" />
      <rect x="340" y="62" width="54" height="3" fill="#B22234" />
      <rect x="340" y="68" width="54" height="3" fill="#B22234" />
      <rect x="340" y="74" width="54" height="3" fill="#B22234" />
      <rect x="340" y="80" width="54" height="3" fill="#B22234" />
      {/* White stripes (background) */}
      <rect x="340" y="50" width="54" height="33" fill="#FFFFFF" opacity="0" />
      {/* Blue canton (top-left rectangle) */}
      <rect x="340" y="50" width="22" height="18" fill="#3C3B6E" />
      {/* One white star on canton */}
      <polygon points="351,55 352.2,58.2 355.5,58.2 352.9,60.2 353.9,63.4 351,61.4 348.1,63.4 349.1,60.2 346.5,58.2 349.8,58.2" fill="#FFFFFF" />
      {/* Thin border around flag */}
      <rect x="340" y="50" width="54" height="33" fill="none" stroke="#e5e7eb" strokeWidth="0.5" />

      {/* Header */}
      <text x="400" y="120" textAnchor="middle" fontSize="14" fill="#6b7280" fontFamily="Georgia, serif" letterSpacing="4">
        NOMADLEXIS
      </text>

      {/* Title */}
      <text x="400" y="168" textAnchor="middle" fontSize="32" fill={color} fontFamily="Georgia, serif" fontWeight="600">
        {lang === 'en' ? 'CERTIFICATE' : 'CERTIFICADO'}
      </text>

      {/* Decorative line */}
      <line x1="250" y1="185" x2="550" y2="185" stroke={color} strokeWidth="1" opacity="0.4" />
      <circle cx="400" cy="185" r="3" fill={color} opacity="0.4" />

      {/* Subtitle */}
      <text x="400" y="215" textAnchor="middle" fontSize="13" fill="#6b7280" fontFamily="Georgia, serif">
        {lang === 'en' ? 'This certificate is awarded to' : 'Este certificado se otorga a'}
      </text>

      {/* Student name */}
      <text x="400" y="268" textAnchor="middle" fontSize="36" fill="#1f2937" fontFamily="Georgia, serif" fontStyle="italic" fontWeight="700">
        {studentName}
      </text>

      {/* Underline for name */}
      <line x1="200" y1="282" x2="600" y2="282" stroke="#d1d5db" strokeWidth="1" />

      {/* Achievement text */}
      <text x="400" y="320" textAnchor="middle" fontSize="13" fill="#6b7280" fontFamily="Georgia, serif">
        {lang === 'en' ? 'for successfully completing' : 'por haber completado exitosamente'}
      </text>

      <text x="400" y="355" textAnchor="middle" fontSize="22" fill={color} fontFamily="Georgia, serif" fontWeight="600">
        {achievement[lang]}
      </text>

      {/* Description */}
      <text x="400" y="390" textAnchor="middle" fontSize="13" fill="#6b7280" fontFamily="Georgia, serif">
        {lang === 'en'
          ? 'of the A1 Intensive English Course'
          : 'del Curso Intensivo de Inglés Nivel A1'}
      </text>

      {/* Decorative divider */}
      <line x1="300" y1="420" x2="500" y2="420" stroke={color} strokeWidth="0.5" opacity="0.3" />

      {/* Date */}
      <text x="400" y="455" textAnchor="middle" fontSize="12" fill="#9ca3af" fontFamily="Georgia, serif">
        {formattedDate}
      </text>

      {/* Seal / stamp effect */}
      <circle cx="400" cy="498" r="25" fill="none" stroke={color} strokeWidth="1.5" opacity="0.3" />
      <circle cx="400" cy="498" r="20" fill="none" stroke={color} strokeWidth="0.5" opacity="0.2" />
      {/* Star in seal */}
      <polygon
        points="400,480 403,491 414,491 405,498 408,509 400,503 392,509 395,498 386,491 397,491"
        fill={color}
        opacity="0.15"
      />
      <text x="400" y="502" textAnchor="middle" fontSize="8" fill={color} opacity="0.4" fontFamily="Georgia, serif">
        A1
      </text>
    </svg>
  )
}

export default function Certificate({ studentName, achievement, date, lang, onClose }: CertificateProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    if (!svgRef.current) return
    setIsDownloading(true)

    try {
      const svgElement = svgRef.current
      const svgData = new XMLSerializer().serializeToString(svgElement)
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)

      // Create canvas from SVG
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = 1600
        canvas.height = 1132
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

        canvas.toBlob((blob) => {
          if (!blob) return
          const link = document.createElement('a')
          link.download = `certificat-${studentName.replaceAll(' ', '-').toLowerCase()}.png`
          link.href = URL.createObjectURL(blob)
          link.click()
          URL.revokeObjectURL(link.href)
          setIsDownloading(false)
        }, 'image/png', 1.0)

        URL.revokeObjectURL(url)
      }
      img.src = url
    } catch {
      setIsDownloading(false)
    }
  }

  const stageColor = '#0055A4'

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      role="button"
      tabIndex={0}
      aria-label={lang === 'en' ? 'Close certificate' : 'Cerrar certificado'}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === 'Escape') && e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-card rounded-2xl max-w-[860px] w-full p-6 space-y-4 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">
            {lang === 'en' ? 'Your Certificate' : 'Tu Certificado'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Certificate preview */}
        <div className="border rounded-lg overflow-hidden bg-white">
          <CertificateSVG
            studentName={studentName}
            achievement={achievement}
            date={date}
            lang={lang}
            color={stageColor}
            svgRef={svgRef}
          />
        </div>

        {/* Download button */}
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isDownloading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Download className="w-5 h-5" />
          )}
          {lang === 'en' ? 'Download as PNG' : 'Descargar en PNG'}
        </button>
      </div>
    </div>
  )
}
